import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaLink, FaUnlink } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  ScheduleModel,
  ShiftEventConnectionRecommendation,
} from '../../../../models/schedule.model';
import scheduleService from '../../../../services/community/scheduleService';
import { Event } from '../../../../types/event';
import Button from '../../../components/buttons/Button';

type ScheduleConnectEventsTabProps = {
  schedule: ScheduleModel;
  shifts: Event[];
  onConnectionsApplied: () => Promise<void>;
};

function formatDateTime(date: string, language: string): string {
  return new Intl.DateTimeFormat(language, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(date));
}

function formatRange(startTime: string, endTime: string | undefined, language: string): string {
  const start = formatDateTime(startTime, language);
  if (!endTime) {
    return start;
  }

  return `${start} - ${formatDateTime(endTime, language)}`;
}

export default function ScheduleConnectEventsTab({
  schedule,
  shifts,
  onConnectionsApplied,
}: ScheduleConnectEventsTabProps): JSX.Element {
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [savingShiftId, setSavingShiftId] = useState<number | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [recommendations, setRecommendations] = useState<ShiftEventConnectionRecommendation[]>([]);

  const sortedRecommendations = useMemo(() => {
    return recommendations
      .slice()
      .sort(
        (first, second) =>
          new Date(first.shiftStartTime).getTime() - new Date(second.shiftStartTime).getTime(),
      );
  }, [recommendations]);

  const loadRecommendations = async () => {
    try {
      setLoading(true);
      const data = await scheduleService.listShiftEventConnectionRecommendations(schedule.scheduleId);
      setRecommendations(data);
    } catch {
      toast.error(t('general.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecommendations();
  }, [schedule.scheduleId, shifts.length]);

  const refreshAll = async () => {
    await Promise.all([loadRecommendations(), onConnectionsApplied()]);
  };

  const handleConnect = async (shiftEventId: number, eventId: number) => {
    try {
      setSavingShiftId(shiftEventId);
      await scheduleService.connectShiftToEvent(schedule.scheduleId, shiftEventId, { eventId });
      await refreshAll();
      toast.success(t('page.event.schedules.connectEvents.messages.connected'));
    } catch {
      toast.error(t('general.error'));
    } finally {
      setSavingShiftId(null);
    }
  };

  const handleDisconnect = async (shiftEventId: number) => {
    try {
      setSavingShiftId(shiftEventId);
      await scheduleService.connectShiftToEvent(schedule.scheduleId, shiftEventId, { eventId: null });
      await refreshAll();
      toast.success(t('page.event.schedules.connectEvents.messages.disconnected'));
    } catch {
      toast.error(t('general.error'));
    } finally {
      setSavingShiftId(null);
    }
  };

  const handleConnectAll = async () => {
    try {
      setBulkSaving(true);
      const result = await scheduleService.connectAllShiftsToRecommendedEvents(schedule.scheduleId);
      await refreshAll();

      if (result.connectedCount === 0) {
        toast.info(t('page.event.schedules.connectEvents.messages.nothingConnected'));
        return;
      }

      toast.success(
        t('page.event.schedules.connectEvents.messages.bulkConnected', {
          connected: result.connectedCount,
          alreadyConnected: result.skippedAlreadyConnectedCount,
          noRecommendation: result.noRecommendationCount,
        }),
      );
    } catch {
      toast.error(t('general.error'));
    } finally {
      setBulkSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-primary-100 bg-primary-50 p-4 text-sm text-primary-600">
        {t('general.loading')}
      </div>
    );
  }

  if (!sortedRecommendations.length) {
    return (
      <div className="rounded-2xl border border-primary-100 bg-primary-50 p-4 text-sm text-primary-600">
        {t('page.event.schedules.emptyShifts')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary-100 bg-primary-50 p-4">
        <div>
          <p className="text-sm font-semibold text-primary-900">
            {t('page.event.schedules.connectEvents.title')}
          </p>
          <p className="text-xs text-primary-600">
            {t('page.event.schedules.connectEvents.subtitle')}
          </p>
        </div>
        <Button variant="primary" onClick={handleConnectAll} disabled={bulkSaving || loading}>
          <FaLink className="h-3.5 w-3.5" />
          {t('page.event.schedules.connectEvents.connectAll')}
        </Button>
      </div>

      {sortedRecommendations.map((item) => {
        const isSavingRow = savingShiftId === item.shiftEventId;

        return (
          <div
            key={item.shiftEventId}
            className="flex flex-col gap-3 rounded-2xl border border-primary-100 bg-secondary-50 p-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-primary-900">{item.shiftTitle}</p>
                <p className="text-xs text-primary-600">
                  {formatRange(item.shiftStartTime, item.shiftEndTime, i18n.language)}
                </p>
              </div>
              {item.overlapMinutes ? (
                <span className="rounded-full bg-secondary-200 px-2 py-1 text-xs font-medium text-primary-700">
                  {t('page.event.schedules.connectEvents.overlap', {
                    minutes: item.overlapMinutes,
                  })}
                </span>
              ) : null}
            </div>

            {item.connectedEvent ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-secondary-200 bg-opacity-50 p-3">
                <div>
                  <p className="text-xs text-primary-500">
                    {t('page.event.schedules.connectEvents.connectedTo')}
                  </p>
                  <p className="text-sm font-medium text-primary-900">{item.connectedEvent.title}</p>
                  <p className="text-xs text-primary-600">
                    {formatRange(
                      item.connectedEvent.startTime,
                      item.connectedEvent.endTime,
                      i18n.language,
                    )}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => handleDisconnect(item.shiftEventId)}
                  disabled={isSavingRow || bulkSaving}
                >
                  <FaUnlink className="h-3.5 w-3.5" />
                  {t('page.event.schedules.connectEvents.disconnect')}
                </Button>
              </div>
            ) : item.recommendedEvent ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-secondary-200 bg-opacity-50 p-3">
                <div>
                  <p className="text-xs text-primary-500">
                    {t('page.event.schedules.connectEvents.recommendedEvent')}
                  </p>
                  <p className="text-sm font-medium text-primary-900">{item.recommendedEvent.title}</p>
                  <p className="text-xs text-primary-600">
                    {formatRange(
                      item.recommendedEvent.startTime,
                      item.recommendedEvent.endTime,
                      i18n.language,
                    )}
                  </p>
                </div>
                <Button
                  variant="primary-outline"
                  onClick={() => handleConnect(item.shiftEventId, item.recommendedEvent!.eventId)}
                  disabled={isSavingRow || bulkSaving}
                >
                  <FaLink className="h-3.5 w-3.5" />
                  {t('page.event.schedules.connectEvents.connect')}
                </Button>
              </div>
            ) : (
              <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-3 text-xs text-primary-600">
                {t('page.event.schedules.connectEvents.noRecommendation')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
